import React from 'react';

import { CaretDownIcon } from '@phosphor-icons/react';

import { MPFIconButton } from 'volto-ministerio-publico-federal/components/UI';
import { MenuLink } from './Menu';
import {
  isHomeItem,
  menuItemKey,
  sanitizeId,
  withHomeItem,
  type MenuItem,
} from './mainMenu';
import { useAccordion, type Accordion } from './hooks/useAccordion';

export interface MenuMobileProps {
  navItems: MenuItem[];
  /* O Header usa esta ref para saber se um clique caiu dentro do painel. */
  panelRef: React.RefObject<HTMLElement | null>;
  /* Fecha o painel ao seguir um link — inclusive para a página atual, em que
     o `pathname` não muda e o efeito do Header não dispara. */
  onNavigate: () => void;
}

interface MenuMobileNodeProps {
  entry: MenuItem;
  level: number;
  accordion: Accordion;
  onNavigate: () => void;
}

const MenuMobileNode: React.FC<MenuMobileNodeProps> = ({
  entry,
  level,
  accordion,
  onNavigate,
}) => {
  const key = menuItemKey(entry);
  const children = isHomeItem(entry) ? [] : entry.items ?? [];
  const hasChildren = children.length > 0;
  const open = hasChildren && accordion.isOpen(key);
  const listId = `mm-${sanitizeId(key)}`;

  const itemClass = [
    'mpf-topbar__mobile-menu-item',
    `mpf-topbar__mobile-menu-item--level-${level}`,
    hasChildren ? 'mpf-topbar__mobile-menu-item--branch' : '',
    open ? 'mpf-topbar__mobile-menu-item--open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li className={itemClass}>
      <div className="mpf-topbar__mobile-menu-row">
        <MenuLink
          node={entry}
          className="mpf-topbar__mobile-menu-link"
          onClick={onNavigate}
        />
        {hasChildren && (
          <MPFIconButton
            variant="unstyled"
            shape="square"
            className="mpf-topbar__mobile-menu-toggle"
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            label={`${open ? 'Recolher' : 'Expandir'} submenu de ${entry.title}`}
            onClick={() => accordion.toggle(key)}
            icon={<CaretDownIcon className="mpf-topbar__mobile-menu-caret" />}
          />
        )}
      </div>

      {open && (
        <ul id={listId} className="mpf-topbar__mobile-menu-list">
          {children.map((child) => (
            <MenuMobileNode
              key={menuItemKey(child)}
              entry={child}
              level={level + 1}
              accordion={accordion}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

/*
------------------------------------------------------------
Menu principal no mobile
------------------------------------------------------------
Abaixo de 1024px o mega menu não existe (o painel de colunas é
`display: none`). A árvore inteira vira um acordeão com os mesmos níveis do
desktop: o título de cada linha é link para a página, e o botão ao lado só
abre e recolhe os filhos. Vários ramos podem ficar abertos ao mesmo tempo.
O painel nasce colado na barra azul, e o fundo atrás dele cobre a página.
------------------------------------------------------------
*/
export const MenuMobile: React.FC<MenuMobileProps> = ({
  navItems,
  panelRef,
  onNavigate,
}) => {
  const accordion = useAccordion();

  return (
    <>
      <div className="mpf-topbar__mobile-menu-backdrop" aria-hidden="true" />
      <nav
        id="mobile-menu"
        ref={panelRef as React.RefObject<HTMLElement>}
        className="mpf-topbar__mobile-menu"
        aria-label="Menu principal"
      >
        <div className="mpf-topbar__mobile-menu-viewport">
          <ul className="mpf-topbar__mobile-menu-list">
            {withHomeItem(navItems).map((entry) => (
              <MenuMobileNode
                key={menuItemKey(entry)}
                entry={entry}
                level={1}
                accordion={accordion}
                onNavigate={onNavigate}
              />
            ))}
          </ul>
        </div>
      </nav>
    </>
  );
};

export default MenuMobile;
